import {
  Text,
  TokenTextAppearance,
  DesignTokenSizeEnum,
  TextAlignment,
} from '@volkswagen-onehub/components-core';
import { useTribalContext } from '@/context/tribal';
import useGlobalStore from '@/stores/globalStore';
import { AutoScrollContainer, CapiImage } from '@/modules/components';
import FinanceExplained from '@/modules/components/FinanceExplained/FinanceExplained';
import {
  Box,
  FinanceCalculationsUnavailable,
  OfferComparisonCard,
} from '@tribal-worldwide-london/ui-component-library-vwg';
import { CAPI_PREVIEW_IMAGE_REL, URL_GREYED_OUT_CAR } from '@/constants/images';
import {
  GetCalculationForPcpProductRequestDto,
  GetProductsForCapCodeAndPriceRequestDto,
  Vehicle,
} from '@tribal-worldwide-london/fa-finance-service-vwg';
import {
  GetCarConfigurationFromStockOrderNumberRequestDto,
  GetConfigurationFromMarketingShortCodeRequestDto,
} from '@tribal-worldwide-london/fa-car-service-vwg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CardWrapper,
  TitleWrapper,
  VehicleWrap,
  VehicleUnavailable,
  FinanceCalculationsUnavailableWrap,
} from './FinanceOptions.styles';
import { Info } from '@volkswagen-onehub/icons-core';
import {
  EventCommonProps,
  performVWBasicButtonClick,
  performVWBasicErrorMessageLoad,
  performVWBasicLayerLoad,
  performVWBasicLinkClick,
  performVWBasicVideoPlayerPauseClick,
  performVWBasicVideoPlayerPlayClick,
  performVWInfoButtonCLick,
  performVWBasicLayerClose,
  performVWBasicVideoPlayerMilestoneReached,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { RetailerFinanceTerms } from '../../single/YourFinanceCalculation/YourFinanceCalculation.styles';
import { getBespokeSpecItemsCodes, getFinanceCtaUnavailableUrl, isStockCar } from '@/modules/utils/storeUtils';
import {
  FinanceDeeplinkUrl,
  GetFinanceDeepLinkUrlDTO,
} from '@tribal-worldwide-london/fa-agent-service-vwg';
import { ApiResponseDTO } from '@tribal-worldwide-london/fa-api-service';

const FinanceOptions = () => {
  const {
    contentManager,
    financeService,
    analyticsManager,
    analyticsBuilder,
    carService,
    configManager,
    agentService,
  } = useTribalContext();
  const configurations = useGlobalStore((state) => state.app.configurations);
  const activeSlideIndex = useGlobalStore((state) => state.activeSlideIndex);

  const [mergedResults, setMergedResults] = useState({});
  const [financeResults, setFinanceResults] = useState({});
  const [shouldShowFinanceExplained, setShowFinanceExplained] = useState(false);
  const [clickedConfiguration, setClickedConfiguration] = useState<number>();
  const [modalIsVisible, setModalIsVisible] = useState();
  const [isFinanceDeeplinkUrlLoading, setIsFinanceDeeplinkUrlLoading] =
    useState(false);

  const handleModalVisibility = (isVisible: any) => {
    setModalIsVisible(isVisible);
  };

  const { retailers } = useGlobalStore((state) => state.app);

  // Analytics data object
  const analyticsCommonFields = useMemo(
    () =>
      (sectionName: SectionNames, configuration?: any): EventCommonProps => ({
        sectionName,
        analyticsManager,
        analyticsBuilder,
        retailers,
        configuration: configuration ? configuration : {},
      }),
    [analyticsManager, analyticsBuilder, retailers]
  );

  const handleClickFinanceCtaUnavailable = useCallback(
    (configuration) => {
      performVWBasicLinkClick({
        label: contentManager.get(
          'finance-calculations-unavailable-cta-link-text'
        ),
        sectionName: SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION,
        configuration: configuration,
        retailers,
        analyticsManager,
        analyticsBuilder,
      });
      window.open(
        getFinanceCtaUnavailableUrl(
          configManager.get('urls.offersFinanceFA'),
          configuration
        ),
        '_self'
      );
    },
    [analyticsCommonFields, configManager]
  );

  const handleClickFinanceCta = async (configuration, index) => {
    const url = await fetchDeeplinkUrl(configuration, index);
    performVWBasicLinkClick({
      label: contentManager.get(
        'finance-calculations-unavailable-cta-link-text'
      ),
      sectionName: SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION,
      configuration: configuration,
      retailers,
      analyticsManager,
      analyticsBuilder,
    });
    if (url) {
      window.open(url, '_self');
    }
  };

  const fetchDeeplinkUrl = async (configuration, index) => {
    const calculation = configuration.finance[0];
    const vehicleType = isStockCar(configuration) ? 'STOCK' : 'CONFIGURATION';
    const vehicleId = isStockCar(configuration)
      ? configuration.orderNumber
      : configuration.marketingShortCode;
    let productId = calculation.productId || '';
    const bespokeItemsCodes = getBespokeSpecItemsCodes(configuration.bespokeSpecItems || []);    
    try {
      setIsFinanceDeeplinkUrlLoading(true);
      const response: ApiResponseDTO<FinanceDeeplinkUrl> =
        await agentService.getRedirectFinanceDeepLinkUrl(
          new GetFinanceDeepLinkUrlDTO(
            vehicleType,
            vehicleId || '',
            'PCP',
            !calculation.productLabel ? productId : '',
            calculation.productLabel,
            calculation.parameters.periodOfMonths,
            calculation.parameters.annualMileage,
            calculation.parameters.deposit!,
            undefined,
            undefined,
            bespokeItemsCodes
          )
        );
      if (response.success) {
        return response.data ? response.data.url : '';
      }
      return '';
    } catch {
      return '';
    } finally {
      setIsFinanceDeeplinkUrlLoading(false);
    }
  };

  useEffect(() => {
    if (modalIsVisible == false) {
      setShowFinanceExplained(false);
    }
  }, [modalIsVisible, shouldShowFinanceExplained]);

  useEffect(() => {
    async function fetchCars() {
      const carConfigurations = await Promise.all(
        configurations.map(async (configuration, index) => {
          if (configuration.orderNumber) {
            const bespokeItemsCodes = getBespokeSpecItemsCodes(configuration.bespokeSpecItems || []);

            const req = new GetCarConfigurationFromStockOrderNumberRequestDto(
              //@ts-ignore
              configuration.orderNumber,
              true,
              false,
              bespokeItemsCodes
            );
            const response =
              await carService.getCarConfigurationFromStockOrderNumber(req);
            return { index, response };
          } else {
            const req = new GetConfigurationFromMarketingShortCodeRequestDto(
              //@ts-ignore
              configuration.marketingShortCode,
              true,
              false
            );
            const response =
              await carService.getCarConfigurationFromMakertingShortCode(req);
            return { index, response };
          }
        })
      );
      const result = carConfigurations.reduce((acc, { index, response }) => {
        acc[index] = response;
        return acc;
      }, {} as { [key: number]: any });
      setFinanceResults(result);
      return result;
    }

    async function fetchCalc() {
      // First, fetch car configurations using fetchCars
      const financeResults = await fetchCars();
      //fetch products for each configuration capcode
      const financeProductsResults = await Promise.all(
        configurations.map((configuration, index) => {
          const price =
            financeResults?.[index]?.data?.price?.finance.personalPrice -
              (configuration.pdbData?.pricing?.allowance || 0);
          let vehicle: Vehicle = {
            capCode: financeResults?.[index]?.data?.summary?.capCode?.value,
            price,
          };
          return financeService
            .getProductsForCapCodeAndPrice(
              new GetProductsForCapCodeAndPriceRequestDto('PERSONAL', [vehicle])
            )
            .then((res: any) => {
              return { res };
            });
        })
      );
      // Now perform the calculations using the financeResults from fetchCars
      const calculations = await Promise.all(
        configurations.map((configuration, index) => {
          let finance = configuration.finance;
          const productlabel = finance && finance[0] && finance[0].productLabel;
          if (
            !financeProductsResults[index].res.data ||
            (configuration.finance &&
              configuration.finance[0] &&
              configuration.finance[0].parameters == null)
          ) {
            // if there is no finance, we'll override the response so we can display the
            // finance calc unavail component
            return Promise.resolve({ [index]: { success: false } });
          }

          const financeProduct = financeProductsResults[index].res.data.find(
            (product) => product.label === productlabel
          );

          const req = new GetCalculationForPcpProductRequestDto(
            financeResults?.[index]?.data.summary.capCode.value,
            financeProduct?.productId,
            financeResults?.[index]?.data.price.finance.personalPrice -
                (configuration.pdbData?.pricing?.allowance || 0),
            finance?.[0]?.parameters?.deposit || 0,
            0,
            finance?.[0]?.parameters?.periodOfMonths || 0,
            finance?.[0]?.parameters?.annualMileage || 0
          );
          return financeService
            .getCalculationForPcpProduct(req)
            .then((res: any) => {
              return { [index]: res };
            });
        })
      );
      // Merge all individual objects into one so we can use the index to map over and
      // give the correct values to the OfferComparisonCard
      const mergedResults = calculations?.reduce((acc, cur) => {
        return { ...acc, ...cur };
      }, {});
      // setting results
      setMergedResults(mergedResults);
      return mergedResults;
    }

    fetchCalc();
  }, [configurations]);

  const financeExplainedLease = {
    title:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-title'
      ) || '',
    titleBold:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-title-bold'
      ) || '',
    video:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-video'
      ) || '',
    bodySubtitle:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-body-subtitle'
      ) || '',
    body:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-body'
      ) || '',
    ctaText:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-cta-text'
      ) || '',
    ctaLink:
      contentManager.get(
        'your-finance-calculation-section-finance-explained-product-tabs-cta-link'
      ) || '',
  };

  const analyticsFunctions = {
    clickFinanceExplained: () => {
      performVWInfoButtonCLick(
        analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        )
      );
    },
    videoPauseCallback: (e, video) => {
      const eventData = {
        playerName: e.playerName ?? null,
        title: 'Volkswagen - Car finance made simple',
        id: video ?? null,
        platform: e.platform ?? null,
        url: e.url ?? null,
        time_played: e.timePlayed ?? null,
        length: e.length ?? null,
        playReason: null,
        viewable: null,
        resolution: null,
        milestone: null,
      };
      performVWBasicVideoPlayerPauseClick({
        videoFields: eventData,
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION,
          configurations[clickedConfiguration || 0]
        ),
      });
    },
    videoPlayCallback: (e, video) => {
      const eventData = {
        playerName: e.playerName ?? null,
        title: 'Volkswagen - Car finance made simple',
        id: video ?? null,
        platform: e.platform ?? null,
        url: e.url ?? null,
        time_played: e.timePlayed ?? null,
        length: e.length ?? null,
        playReason: null,
        viewable: null,
        resolution: null,
        milestone: null,
      };
      performVWBasicVideoPlayerPlayClick({
        videoFields: eventData,
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION,
          configurations[clickedConfiguration || 0]
        ),
      });
    },
    videoMilestoneCallback: (e, video, milestone) => {
      const eventData = {
        playerName: e.playerName ?? null,
        title: 'Volkswagen - Car finance made simple',
        id: video ?? null,
        platform: e.platform ?? null,
        url: e.url ?? null,
        time_played: e.timePlayed ?? null,
        length: e.length ?? null,
        playReason: null,
        viewable: null,
        resolution: null,
        milestone: null,
      };
      performVWBasicVideoPlayerMilestoneReached({
        videoFields: eventData,
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION,
          configurations[clickedConfiguration || 0]
        ),
      });
    },
    clickFindOutMore: (ctaLink: string, ctaText: string) => {
      performVWBasicButtonClick({
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        ),
        label: ctaText,
      });
    },
    InfoMessageLoadForConfiguration: () => {
      performVWBasicLayerLoad({
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        ),
      });
    },
    closeClickForConfiguration: () => {
      performVWBasicLayerClose({
        ...analyticsCommonFields(
          SectionNames.COMPARE_PAGE_YOUR_FINANCE_CALCULATION
        ),
      });
    },
  };

  useEffect(() => {
    Object.keys(mergedResults).forEach((index) => {
      if (
        (mergedResults[index] && mergedResults[index].success == false) ||
        !mergedResults[index] ||
        !mergedResults[index].data.calculation.details ||
        !mergedResults[index].data.calculation.description ||
        !mergedResults[index].data.calculation.disclaimer ||
        !mergedResults[index].data.calculation.shortDisclaimer
      ) {
        performVWBasicErrorMessageLoad({
          ...analyticsCommonFields,
          error: {
            message: contentManager.get(
              'finance-calculations-unavailable-cta-title'
            ),
          },
          analyticsManager,
          analyticsBuilder,
        });
      }
    });
  }, [mergedResults]);

  return (
    <>
      <TitleWrapper>
        <Text appearance={TokenTextAppearance.headline400}>
          {contentManager.get('your-finance-calculation-title')}{' '}
          <strong>
            {contentManager.get('your-finance-calculation-titleBold')}
          </strong>
        </Text>
        <Text appearance={TokenTextAppearance.bigcopy100}>
          {contentManager.get('your-finance-calculation-subtitle')}
        </Text>
      </TitleWrapper>
      <AutoScrollContainer
        activeSlideIndex={activeSlideIndex}
        responsiveProps={{
          mobile: {
            slidesPerPage: 2,
            gap: DesignTokenSizeEnum.static150,
          },
          desktop: {
            slidesPerPage: 3,
            gap: DesignTokenSizeEnum.static300,
          },
        }}
      >
        {configurations.map((configuration, index) => {
          const previewImage = configuration.pdbData?.capi?.images.find(
            ({ rel }: CapiImage) => rel === CAPI_PREVIEW_IMAGE_REL
          );

          if (configuration.pdbData?.derivative == null) {
            return (
              <VehicleUnavailable key={index}>
                <VehicleWrap>
                  <Text
                    textAlign={TextAlignment.center}
                    bold
                    appearance={TokenTextAppearance.bigcopy100}
                  >
                    {`This ${configuration.modelTitle} ${configuration.derivativeTitle || configuration.trimTitle} is no longer available`}
                  </Text>
                  <img src={URL_GREYED_OUT_CAR}></img>
                </VehicleWrap>
              </VehicleUnavailable>
            );
          }

          // if there is no finance options avail, show the unavail component.
          if (mergedResults[index] && mergedResults[index].success == false) {
            
            // if all cars finance is unavailable
            const allUnavailable = Object.keys(mergedResults).every(key => mergedResults[key].success == false)
            
            return (
              <FinanceCalculationsUnavailableWrap
                allUnavailable={allUnavailable}
                key={index}
              >
                <FinanceCalculationsUnavailable
                  title={
                    configuration.finance?.length === 0 || configuration.finance === null
                      ? contentManager.get(
                        'finance-calc-configuration-finance-unavail-title'
                      )
                      : contentManager.get('finance-calc-unavail-title')
                  }
                  subtitle={contentManager.get('finance-calc-unavail-subtitle')}
                  linkText={contentManager.get('finance-calc-unavail-linkText')}
                  handleFinanceCta={() =>
                    handleClickFinanceCtaUnavailable(configuration)
                  }
                  borderRadius="24px"
                  height="100%"
                  column="2"
                />
              </FinanceCalculationsUnavailableWrap>
            );
          }
          //else
          return (
            <CardWrapper key={index}>
              {mergedResults[index] &&
              mergedResults[index].data.calculation.details &&
              mergedResults[index].data.calculation.description &&
              mergedResults[index].data.calculation.disclaimer &&
              mergedResults[index].data.calculation.shortDisclaimer ? (
                <OfferComparisonCard
                  column="2"
                  key={configuration.modelTitle + '-' + index}
                  borderRadius="24px"
                  modelTitle={
                    configuration.modelTitle + ' ' + configuration.trimTitle
                  }
                  derivativeTitle={
                    configuration.pdbData?.derivative
                      ? configuration.pdbData?.derivative.title
                      : ''
                  }
                  productDetail={
                    mergedResults[index].data.calculation.description
                  }
                  terms={mergedResults[index].data.calculation.details}
                  disclaimer={
                    mergedResults[index].data.calculation.shortDisclaimer
                  }
                  termsClosedButtonText={contentManager.get('see-full-terms')}
                  termsOpenButtonText={contentManager.get('hide-full-terms')}
                  termsChildren={
                    mergedResults[index].data.calculation.disclaimer
                  }
                  vehicleImageUrl={
                    previewImage?.uri ? previewImage?.uri : URL_GREYED_OUT_CAR
                  }
                  financeCtaText={contentManager.get(
                    'finance-calc-unavail-linkText'
                  )}
                  handleFinanceCta={() => {
                    setClickedConfiguration(index);
                    handleClickFinanceCta(configuration, index);
                  }}
                  isFinanceDeeplinkUrlLoading={
                    isFinanceDeeplinkUrlLoading &&
                    index === clickedConfiguration
                  }
                  hasCalculationError={false}
                  financeExplainedIcon={<Info />}
                  productName="Solutions PCP"
                  handleFinanceExplainedCta={() => {
                    setShowFinanceExplained(true);
                    setClickedConfiguration(index);
                  }}
                  optionalExtrasText={
                    financeResults[index].data.price.display.personalPrice !== financeResults[index].data.price.finance.personalPrice
                      ? contentManager.get('optional-extras-text')
                      : ''
                  }
                ></OfferComparisonCard>
              ) : (
                <FinanceCalculationsUnavailable
                  title={
                    configuration.finance?.length === 0 || configuration.finance === null
                      ? contentManager.get(
                        'finance-calc-configuration-finance-unavail-title'
                      )
                      : contentManager.get('finance-calc-unavail-title')
                  }
                  subtitle={contentManager.get('finance-calc-unavail-subtitle')}
                  linkText={contentManager.get('finance-calc-unavail-linkText')}
                  handleFinanceCta={() => {
                    handleClickFinanceCtaUnavailable(configuration);
                  }}
                  borderRadius="24px"
                  height="100%"
                />
              )}
            </CardWrapper>
          );
        })}
      </AutoScrollContainer>

      <Box mt={DesignTokenSizeEnum.static400}>
        <RetailerFinanceTerms appearance={TokenTextAppearance.label150}>
          {retailers[0]?.fcaTermsAndConditions}
        </RetailerFinanceTerms>
      </Box>

      {shouldShowFinanceExplained && (
        <FinanceExplained
          isModalVisible={handleModalVisibility}
          productTabs={[financeExplainedLease]}
          productType="PCP"
          ctaAriaText="Finance Explained - Find Out More"
          analyticsFunctions={analyticsFunctions}
        />
      )}
    </>
  );
};

export default FinanceOptions;
